import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Button, Carousel, Input, Modal} from 'antd';
import FormError from '../FormError';
import {authErrors} from '../../redux/selectors/error';
import {isModalOpened} from '../../redux/selectors/nav';
import {makeAction} from '../../redux/actions/makeAction';
import {
  CLOSE_MODAL,
  REQUEST_RESET_CODE,
  OPEN_MODAL,
  RESET_PASSWORD,
  VALIDATE_CODE,
} from '../../redux/actions/actionTypes';
import {
  FORGOT_PASSWORD_MODAL_ID,
  LoginModalId,
  API_CODE_LENGTH,
  VerificationCodeType,
} from '../../services/constants';
import VerificationCodeForm from './common/VerificationCodeForm';
import {withTranslation} from 'react-i18next';
import TranslatedText from '../TranslatedText';
import {ReactComponent as CloseIcon} from '../../assets/img/close.svg';
import {ReactComponent as ArrowLeft} from '../../assets/img/arrowLeft.svg';
import {ReactComponent as ArrowRight} from '../../assets/img/arrowRight.svg';

const defaultState = {
  email: '',
  code: ' '.repeat(API_CODE_LENGTH),
  stepOneInProgress: false,
  code_invalid: false,
};

class ForgotPasswordModal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {...defaultState};

    this.stepOneSubmit = this.stepOneSubmit.bind(this);
    this.stepTwoSubmit = this.stepTwoSubmit.bind(this);
    this.stepTwoResend = this.stepTwoResend.bind(this);
    this.stepThreeSubmit = this.stepThreeSubmit.bind(this);
    this.onCodeChange = this.onCodeChange.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.carousel = React.createRef();
  }

  stepOneSubmit(event) {
    event.preventDefault();
    this.setState({stepOneInProgress: true});
    this.props.requestResetCode({
      ...Object.fromEntries(new FormData(event.target)),
      callback: () => {
        this.setState({stepOneInProgress: false});
        this.carousel.current.next();
      },
      onError: () => {
        this.setState({stepOneInProgress: false});
      },
    });
  }

  stepTwoSubmit(code) {
    this.setState({code_invalid: false});
    this.props.validateCode({
      code,
      type: VerificationCodeType.RESET_PASSWORD,
      callback: () => {
        this.setState({code});
        this.carousel.current.next();
      },
      onError: () => {
        this.setState({code_invalid: true});
      },
    });
  }

  stepTwoResend() {
    this.carousel.current.prev();
    this.setState({code: defaultState.code});
  }

  stepThreeSubmit(event) {
    event.preventDefault();
    this.props.resetPassword({
      reset_code: this.state.code,
      callback: () => {
        this.props.openModal({
          id: LoginModalId,
          message: {type: 'success', text: this.props.t('modals.forgotPassword.success')},
        });
        this.cleanup();
      },
    });
  }

  onCancel() {
    this.props.closeModal();
    this.cleanup();
  }

  cleanup() {
    this.setState(defaultState);
    this.carousel.current.goTo(0);
  }

  onCodeChange(code) {
    this.setState({code});
  }

  render() {
    return (
      <Modal
        title={<TranslatedText transKey="modals.forgotPassword.title"/>}
        className="nav-modal recover-password-modal"
        width={440}
        visible={this.props.opened}
        onCancel={this.onCancel}
        closeIcon={<CloseIcon/>}
        footer={<React.Fragment>
          <span><TranslatedText transKey="modals.common.alreadyHaveAccount"/></span>
          <a onClick={() => this.props.openModal({id: LoginModalId})}>
            <TranslatedText transKey="modals.common.loginLink"/>
            <ArrowLeft className="action-icon"/>
          </a>
        </React.Fragment>
        }
      >
        <Carousel ref={this.carousel} dots={false} infinite={false}>
          <div id="recover-password-email-slide">
            <div className="recovery-stage-indicator">
              <h3><TranslatedText transKey="modals.common.step" params={{curr: 1, max: 2}}/></h3>
              <p><TranslatedText transKey="modals.forgotPassword.enterEmail"/></p>
            </div>
            <form onSubmit={this.stepOneSubmit}>
              <div className="modal-form-group">
                <FormError.Tooltip errors={this.props.errors} fieldName="email">
                  <Input placeholder={this.props.t('modals.forgotPassword.enterPlaceholder')} name="email" value={this.state.email} onChange={e => this.setState({email: e.target.value})}/>
                </FormError.Tooltip>
                <Button type="primary" block htmlType="submit" loading={this.state.stepOneInProgress}>
                  <TranslatedText transKey="modals.forgotPassword.sendCode"/>
                </Button>
                <div className="submit-subelement">
                  <a onClick={() => this.carousel.current.next()}>
                    <TranslatedText transKey="modals.verification.ihavecode"/>
                  </a>
                </div>
              </div>
            </form>
          </div>
          <div id="recover-password-recovery-code-slide">
            <VerificationCodeForm onChange={this.onCodeChange} code={this.state.code} onSubmit={this.stepTwoSubmit} isInvalid={this.state.code_invalid}>
              <Button type="primary" block htmlType="submit">
                <TranslatedText transKey="modals.verification.next"/>
              </Button>
              <div className="submit-subelement">
                <a onClick={this.stepTwoResend}>
                  <ArrowRight className="action-icon right"/>
                  <TranslatedText transKey="modals.common.startover"/>
                </a>
              </div>
            </VerificationCodeForm>
          </div>
          <div id="recover-password-new-password-slide">
            <div className="recovery-stage-indicator">
              <h3><TranslatedText transKey="modals.common.step" params={{curr: 2, max: 2}}/></h3>
              <p><TranslatedText transKey="modals.forgotPassword.sendPassword"/></p>
            </div>
            <form onSubmit={this.stepThreeSubmit}>
              <div className="modal-form-group">
                <p><TranslatedText transKey="modals.forgotPassword.text"/></p>
              </div>
              <Button type="primary" block htmlType="submit">
                <TranslatedText transKey="modals.forgotPassword.submit"/>
              </Button>
            </form>
          </div>
        </Carousel>
      </Modal>
    );
  }
}

ForgotPasswordModal.propTypes = {
  errors: PropTypes.object.isRequired,
  opened: PropTypes.bool.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  resetPassword: PropTypes.func.isRequired,
  requestResetCode: PropTypes.func.isRequired,
  validateCode: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  opened: isModalOpened(state)(FORGOT_PASSWORD_MODAL_ID),
  errors: authErrors(state),
});

const mapDispatchToProps = {
  closeModal: makeAction(CLOSE_MODAL),
  openModal: makeAction(OPEN_MODAL),
  requestResetCode: makeAction(REQUEST_RESET_CODE),
  resetPassword: makeAction(RESET_PASSWORD),
  validateCode: makeAction(VALIDATE_CODE),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(ForgotPasswordModal));



// WEBPACK FOOTER //
// /src/components/modals/ForgotPasswordModal.jsx